import { ChangeDetectorRef, Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';

import { forkJoin, map } from 'rxjs';
import { TsButtonModule } from 'tsui';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { RelatedToType } from '@shared/enums';
import { ProjectsService } from '@modules/projects/projects.service';
import { DirectivesModule } from '@shared/directives/directives.module';
import { LayoutsService } from '@services/layouts.service';
import * as CustomValidator from '@shared/directives/custom-validators.directive';
import { Pipeline, Project, ProjectTemplate } from '@modules/projects/projects.types';

declare interface DialogDataInterface {
  byPass?: boolean;
  model: any;
  multi_select?: boolean;
  related_to_type: RelatedToType;
  searchTerm?: string;
}

@Component({
  selector: 'add-project-modal',
  standalone: true,
  imports: [
    TsDialogComponent,
    TsButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    FormsModule,
    ReactiveFormsModule,
    DirectivesModule,
  ],
  templateUrl: './add-project-modal.component.html',
  styleUrls: ['./add-project-modal.component.scss'],
})
export class AddProjectModalComponent implements OnInit {
  projectForm: FormGroup;
  pipelines: Pipeline[] = [];
  projectTemplates: ProjectTemplate<any>[] = [];
  isLoading = false;
  isOptionsLoading = true;

  get isSaveBtnDisabled(): boolean {
    return this.isLoading || this.projectForm.invalid || this.projectForm.pristine;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: DialogDataInterface,
    private readonly matDialogRef: MatDialogRef<AddProjectModalComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly projectsService: ProjectsService,
    private readonly layoutsService: LayoutsService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadOptions();
  }

  private buildForm(): void {
    this.projectForm = this.formBuilder.group({
      title: [this.data?.searchTerm || '', [Validators.required, CustomValidator.noWhiteSpaceValidator]],
      pipeline_id: [null],
      template_id: [null, [Validators.required]],
      property: this.formBuilder.group({
        address: this.formBuilder.group({
          address_string: [''],
          street: [''],
          city: [''],
          state: [''],
          postal_code: [''],
          latitude: [''],
          longitude: [''],
        }),
      }),
    });
  }

  private loadOptions(): void {
    this.isOptionsLoading = true;
    forkJoin({
      projectTemplates: this.layoutsService.get<any>(RelatedToType.Project),
      pipelines: this.projectsService.getPipelines(),
    })
      .pipe(
        map(({ projectTemplates, pipelines }) => {
          const templates = this.layoutsService.extractTemplates(projectTemplates);

          return {
            projectTemplates: templates.filter((option) => !option.hidden).sort((a, b) => a.rank - b.rank),
            pipelines,
          };
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ projectTemplates, pipelines }) => {
        this.projectTemplates = projectTemplates;
        this.pipelines = pipelines;
        this.applyDefaultSelections();
        this.isOptionsLoading = false;
        this.changeDetectorRef.markForCheck();
      });
  }

  private applyDefaultSelections(): void {
    if (!this.projectForm.get('pipeline_id').value && this.pipelines?.length) {
      this.projectForm.get('pipeline_id').setValue(this.pipelines[0].id);
    }

    if (!this.projectForm.get('template_id').value && this.projectTemplates?.length) {
      this.projectForm.get('template_id').setValue(this.projectTemplates[0].id);
    }
  }

  setAddress(addressString: string): void {
    this.projectForm.get('property').get('address').get('address_string').setValue(addressString);
  }

  setAddressParts(addressParts: any): void {
    const addressControl = this.projectForm.get('property').get('address');

    addressControl.get('city').setValue(addressParts.locality || addressParts.sublocality_level_1);
    addressControl.get('state').setValue(addressParts.administrative_area_level_1);
    addressControl.get('postal_code').setValue(addressParts.postal_code);
    if (addressParts.street_number && addressParts.route) {
      addressControl.get('street').setValue(`${addressParts.street_number} ${addressParts.route}`);
    } else if (!addressParts.street_number && addressParts.route) {
      addressControl.get('street').setValue(addressParts.route);
    } else {
      addressControl.get('street').setValue('');
    }
    addressControl.get('latitude').setValue(addressParts.lat);
    addressControl.get('longitude').setValue(addressParts.lng);
  }

  saveProject(): void {
    this.projectForm.disable();
    this.isLoading = true;

    const project = this.projectForm.value;
    this.projectsService
      .saveProject(project)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (createdProject: Project) => {
          const projectName = createdProject?.name || createdProject?.title || `Project ${createdProject?.id}`;
          this.matDialogRef.close({
            model: { ...createdProject, name: projectName },
            multi_select: this.data?.multi_select,
          });
        },
        error: () => {
          this.isLoading = false;
          this.projectForm.enable();
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  discard(): void {
    this.matDialogRef.close();
  }
}
